/**
 * OffersListSection — eigene Tab-Sektion für alle bereits erstellten Angebote.
 *
 * Workflow: ProjectDetail tab="offers" → diese Komponente.
 * Funktionen pro Angebot:
 *   • PDF herunterladen
 *   • Rabatt/Provision nachträglich bearbeiten ("Neu berechnen" → PDF wird aktualisiert)
 *   • Kunde digital unterschreiben lassen (SignaturePad)
 *   • Angebot per E-Mail versenden (Modal mit Empfänger-Vorbelegung)
 *   • Angebot löschen (Manager/Admin auch nach Versand)
 *
 * Daten kommen über `offers`-Prop von ProjectDetail. `onUpdate` triggert dort
 * den Silent-Reload.
 */
import { AttachmentPreview } from "./AttachmentPreview";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, FileText, Download, Send, Check, Sparkles, PenTool, Tag,
  Pencil, X, Trash2, Phone, AlertTriangle, Mail, Upload,
  History, ArrowUp, ArrowDown, ChevronDown, RefreshCw, Flame, Sun,
} from "lucide-react";
import api, { fileUrl, freshFileUrl } from "@/lib/api";
import { useReloadBus } from "@/lib/reloadBus";
import { SignaturePad } from "@/components/SignaturePad";
import ManualSignModal from "@/components/ManualSignModal";
import { EasybillPushButton } from "@/components/EasybillPushButton";
import AutofillInput from "@/components/AutofillInput";
import { useAuth } from "@/contexts/AuthContext";
import { isAdmin } from "@/lib/roles";
import RevokeSignatureButton, { RevokedSignatureNote } from "./RevokeSignatureButton";
import { TeamCopyNote } from "@/components/TeamCopyNote";
import { useNavigate } from "react-router-dom";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);


// Iter89z-oflabel (2026-02-26) — PV- vs. WP-Angebote visuell unterscheiden.
// Fallback: Angebote vor Iter89z haben kein offer_type → über Nummern-Präfix.
// PV-Nummern beginnen immer mit "P" (siehe pv_offers._next_offer_number),
// WP-Nummern beginnen mit einer Ziffer.
const isPvOffer = (o) => (
  o?.offer_type === "pv" ||
  (typeof o?.offer_number === "string" && o.offer_number.trim().toUpperCase().startsWith("P"))
);

// Produktlinien-Theme — Farben matchen Novatus-Palette (dunkler Hintergrund,
// gesättigte Akzente). WP = Flamme/orange, PV = Sonne/gelb. Werte als
// Tailwind-Klassen damit Hover-States konsistent bleiben.
const PRODUCT_LINE = {
  wp: {
    label: "WP",
    icon: Flame,
    chipClass: "bg-orange-500/15 text-orange-300 border border-orange-500/40",
    accentBar: "before:bg-orange-500/70",   // 4-px breiter linker Balken
    cardLatest: "border-l-4 border-l-orange-500/70",
    cardDefault: "border-l-4 border-l-orange-500/30",
  },
  pv: {
    label: "PV",
    icon: Sun,
    chipClass: "bg-amber-400/15 text-amber-200 border border-amber-400/40",
    accentBar: "before:bg-amber-400/70",
    cardLatest: "border-l-4 border-l-amber-400/70",
    cardDefault: "border-l-4 border-l-amber-400/30",
  },
};


/**
 * Iter89q (2026-02-24) — Versand-Historie pro Angebot. Zeigt beim Expand
 * alle E-Mail-Versände dieses Angebots mit Zeitstempel + Empfänger. Daten
 * aus `GET /api/offers/{id}/email-sends` (siehe pv_offers.py).
 */
function OfferSendHistory({ offerId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null); // null = not loaded
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    if (!open && rows === null) {
      setLoading(true);
      try {
        const { data } = await api.get(`/offers/${offerId}/email-sends`);
        setRows(Array.isArray(data) ? data : []);
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }
    setOpen((v) => !v);
  };
  return (
    <div className="mt-2" data-testid={`offer-send-history-${offerId}`}>
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1 text-[11.5px] text-zinc-300 hover:text-[#00E575] font-semibold transition"
        data-testid={`offer-send-history-toggle-${offerId}`}
      >
        <History size={12} />
        Versand-Historie
        <ChevronDown size={12} className={`transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-1.5 bg-[#0F0F11] border border-zinc-800 rounded-lg p-2 space-y-1.5"
             data-testid={`offer-send-history-panel-${offerId}`}>
          {loading && <div className="text-[11.5px] text-zinc-500 inline-flex items-center gap-1.5"><Loader2 size={11} className="animate-spin" /> lade…</div>}
          {!loading && rows !== null && rows.length === 0 && (
            <div className="text-[11.5px] text-zinc-500">Noch nicht versendet.</div>
          )}
          {!loading && rows && rows.map((s) => (
            <div key={s.id} className="text-[11.5px] text-zinc-200" data-testid={`offer-send-history-row-${s.id}`}>
              <div className="font-mono text-zinc-100">
                {new Date(s.sent_at).toLocaleString("de-DE", {
                  day: "2-digit", month: "2-digit", year: "numeric",
                  hour: "2-digit", minute: "2-digit",
                })}
              </div>
              <div className="text-zinc-300">
                → <b className="text-zinc-100">{s.to}</b>
                {Array.isArray(s.cc) && s.cc.length > 0 && (
                  <span className="text-zinc-400"> · CC: {s.cc.join(", ")}</span>
                )}
                {Array.isArray(s.bcc) && s.bcc.length > 0 && (
                  <span className="text-zinc-500"> · BCC: {s.bcc.length}×</span>
                )}
                {s.status && s.status !== "sent" && (
                  <span className="ml-1 px-1 py-0.5 rounded bg-red-500/15 text-red-300 text-[10px] font-bold">{s.status}</span>
                )}
                {s.sachbearbeiter_name && (
                  <span className="text-zinc-500"> · SB: {s.sachbearbeiter_name}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/**
 * Iter88zbw Phase B — Verkaufspreis-Ist Editor pro Angebot.
 * Erzeugt via PATCH /api/offers/{id}/upsell eine Mehrverkauf-Zeile.
 */
function UpsellEditor({ offer, onChanged }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(offer.selling_price_gross || "");
  const [saving, setSaving] = useState(false);
  const min = Number(offer.total_gross || 0);
  const save = async (explicitValue = undefined) => {
    setSaving(true);
    try {
      // Iter88zbw-fix: explicit-Wert-Support für den X-Button (Clear),
      // damit save(null) nicht den stale `val` aus dem Render-Closure liest.
      const num = explicitValue === null
        ? null
        : (explicitValue === undefined
            ? (val === "" ? null : Number(val))
            : (explicitValue === "" ? null : Number(explicitValue)));
      await api.patch(`/offers/${offer.id}/upsell`, { selling_price_gross: num });
      toast.success(num == null ? "Mehrverkauf entfernt" : "Verkaufspreis-Ist gespeichert");
      setOpen(false);
      if (num == null) setVal("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };
  if (!open) {
    return (
      <button
        type="button"
        data-testid={`offer-${offer.id}-upsell-open`}
        onClick={() => setOpen(true)}
        className="mt-1.5 text-[11px] font-mono uppercase tracking-wider text-zinc-500 hover:text-[#00E575] transition"
      >
        {offer.selling_price_gross ? "Verkaufspreis-Ist bearbeiten" : "+ Verkaufspreis-Ist / Mehrverkauf"}
      </button>
    );
  }
  return (
    <div className="mt-1.5 rounded-lg bg-[#0E0E12] border border-zinc-800 p-2.5 space-y-2">
      <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-400">
        Kalkulierter Angebotspreis brutto: <b className="text-zinc-100 font-mono">{fmtEur(min)}</b>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min={min}
          step="0.01"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder={`min. ${fmtEur(min)}`}
          data-testid={`offer-${offer.id}-upsell-input`}
          className="h-9 flex-1 rounded-lg bg-[#09090B] border border-zinc-700 px-3 text-sm text-zinc-100 focus:border-[#00E575] outline-none"
        />
        <button type="button" onClick={() => save()} disabled={saving} data-testid={`offer-${offer.id}-upsell-save`}
          className="h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] disabled:opacity-50">
          {saving ? "…" : "Speichern"}
        </button>
        <button type="button" onClick={() => save(null)} disabled={saving || !offer.selling_price_gross}
          data-testid={`offer-${offer.id}-upsell-clear`}
          className="h-9 px-2 rounded-lg text-zinc-500 hover:text-rose-300 text-[12px]" title="Mehrverkauf entfernen">
          ×
        </button>
        <button type="button" onClick={() => setOpen(false)} className="h-9 px-2 rounded-lg text-zinc-400 hover:bg-zinc-800 text-[12px]">
          Abbrechen
        </button>
      </div>
    </div>
  );
}

export default function OffersListSection({ project, customer, offers = [], onUpdate }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManagerOrAdmin = user?.role === "admin" || user?.role === "sales_manager";

  const [signingOfferId, setSigningOfferId] = useState(null);
  const [signSaving, setSignSaving] = useState(false);
  const [manualSignOfferId, setManualSignOfferId] = useState(null);
  const [editingOfferId, setEditingOfferId] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editPct, setEditPct] = useState("");
  const [editEur, setEditEur] = useState("");
  const [editMarginType, setEditMarginType] = useState("percent");
  const [editMarginValue, setEditMarginValue] = useState("");
  const [deletingOfferId, setDeletingOfferId] = useState(null);
  const [caps, setCaps] = useState({ max_discount_percent: null, max_discount_eur: null });

  const [confirmSendOffer, setConfirmSendOffer] = useState(null);
  const [confirmRecipient, setConfirmRecipient] = useState("");
  const [confirmCc, setConfirmCc] = useState("");
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmTemplateId, setConfirmTemplateId] = useState("empty");
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [sending, setSending] = useState(false);

  // Iter88zz4 — Orientierungsangebote parallel laden + versenden.
  const [orientationOffers, setOrientationOffers] = useState([]);
  const [oaLoading, setOaLoading] = useState(true);
  const [oaSending, setOaSending] = useState(null);  // oid or null
  const [oaSendDialog, setOaSendDialog] = useState(null);  // {oa, recipient, cc, message}
  const loadOrientationOffers = () => {
    setOaLoading(true);
    api.get(`/projects/${project.id}/orientation-offers`)
      .then((r) => setOrientationOffers(r.data || []))
      .catch(() => setOrientationOffers([]))
      .finally(() => setOaLoading(false));
  };
  useEffect(() => { loadOrientationOffers(); /* eslint-disable-next-line */ }, [project?.id]);
  // Iter88zz4 — nach jedem Create/Send des Orientierungsangebots feuert
  // der API-Interceptor "*" auf dem Reload-Bus; darauf hin OA-Liste
  // aktualisieren, damit das gerade generierte OA sofort erscheint.
  useReloadBus("project-data", () => { loadOrientationOffers(); });

  const deleteOa = async (oa) => {
    if (!window.confirm(`Orientierungsangebot ${oa.offer_number} wirklich löschen?`)) return;
    try {
      await api.delete(`/orientation-offers/${oa.id}`);
      toast.success(`Orientierungsangebot ${oa.offer_number} gelöscht`);
      setOrientationOffers((rows) => rows.filter((r) => r.id !== oa.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const openOaSendDialog = (oa) => {
    setOaSendDialog({
      oa,
      recipient: customer?.email || "",
      cc: "",
      subject: `Ihr Orientierungsangebot ${oa.offer_number} – NOVATUS Energie`,
      message: "",
    });
  };

  const sendOrientationOffer = async () => {
    if (!oaSendDialog?.oa || !oaSendDialog.recipient) return;
    setOaSending(oaSendDialog.oa.id);
    try {
      const r = await api.post(`/orientation-offers/${oaSendDialog.oa.id}/send-email`, {
        recipient_email: oaSendDialog.recipient.trim(),
        cc_email: (oaSendDialog.cc || "").trim() || null,
        subject: oaSendDialog.subject || null,
        body: oaSendDialog.message || null,
      });
      if (r.data?.ok) {
        toast.success(r.data.message || "Orientierungsangebot versendet");
        setOaSendDialog(null);
        loadOrientationOffers();
        onUpdate?.();
      } else {
        toast.error(r.data?.message || "Versand fehlgeschlagen");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setOaSending(null);
    }
  };

  useEffect(() => {
    api.get("/email-templates").then((r) => setEmailTemplates(r.data?.templates || [])).catch(() => {});
  }, []);

  const editPanelRefs = useRef({});

  // Discount caps for the edit panel.
  useEffect(() => {
    api.get("/sales-context")
      .then((r) => setCaps(r.data || {}))
      .catch(() => {});
  }, []);

  const openEditOffer = (o) => {
    setEditingOfferId(o.id);
    setEditPct(o.discount_percent_requested ? String(o.discount_percent_requested) : "");
    setEditEur(o.discount_eur_requested ? String(o.discount_eur_requested) : "");
    const m = o.margin_applied || {};
    setEditMarginType(m.type || "percent");
    setEditMarginValue(m.value != null ? String(m.value) : "");
    setTimeout(() => {
      const el = editPanelRefs.current[o.id];
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 60);
  };

  const saveEditedOffer = async () => {
    if (!editingOfferId) return;
    setEditSaving(true);
    try {
      const body = {
        discount_percent: parseFloat(editPct) || 0,
        discount_eur: parseFloat(editEur) || 0,
      };
      if (isManagerOrAdmin && editMarginValue !== "") {
        body.margin_type = editMarginType;
        body.margin_value = parseFloat(editMarginValue);
      }
      const r = await api.patch(`/offers/${editingOfferId}/recalc`, body);
      if (r.data.discount_was_capped) {
        toast.warning("Rabatt wurde auf das vom Vertriebsleiter erlaubte Maximum begrenzt.");
      } else {
        toast.success("Angebot neu berechnet & PDF aktualisiert");
      }
      setEditingOfferId(null);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setEditSaving(false);
    }
  };

  const signOffer = async (offerId, dataUrl, signerName) => {
    setSignSaving(true);
    try {
      await api.post(`/offers/${offerId}/sign`, {
        png_base64: dataUrl,
        signer_name: signerName,
      });
      toast.success("Angebot digital unterschrieben");
      setSigningOfferId(null);
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unterschrift fehlgeschlagen");
    } finally {
      setSignSaving(false);
    }
  };

  const deleteOffer = async (o) => {
    if (!window.confirm(`Angebot ${o.offer_number} wirklich löschen?`)) return;
    setDeletingOfferId(o.id);
    try {
      await api.delete(`/offers/${o.id}`);
      toast.success("Angebot gelöscht");
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setDeletingOfferId(null);
    }
  };

  // Iteration 84 — Recovery + Liefer-/Leistungsvertrag on demand.
  const [busyOfferId, setBusyOfferId] = useState(null); // {id, action}

  const rebuildSignedPdf = async (o) => {
    setBusyOfferId({ id: o.id, action: "rebuild" });
    try {
      const r = await api.post(`/offers/${o.id}/rebuild-signed-pdf`);
      toast.success("Signiertes PDF wurde neu erzeugt");
      if (r.data?.pdf_storage_path) {
        window.open(freshFileUrl(r.data.pdf_storage_path), "_blank", "noopener");
      }
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Neu-Erzeugung fehlgeschlagen");
    } finally {
      setBusyOfferId(null);
    }
  };

  const buildLieferLeistung = async (o) => {
    setBusyOfferId({ id: o.id, action: "liefer" });
    try {
      const r = await api.post(`/offers/${o.id}/liefer-leistung-vertrag`);
      toast.success("Liefer- und Leistungsvertrag erstellt");
      if (r.data?.pdf_storage_path) {
        window.open(freshFileUrl(r.data.pdf_storage_path), "_blank", "noopener");
      }
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vertrags-Erstellung fehlgeschlagen");
    } finally {
      setBusyOfferId(null);
    }
  };

  // Iter88zz9 — PDF neu erzeugen für UN-signierte Angebote. Nach Katalog-
  // Änderungen (Beschreibungen, Preise) will der Vertrieb das PDF sofort
  // frisch generieren, ohne das Angebot neu anzulegen.
  const regenerateOfferPdf = async (o) => {
    setBusyOfferId({ id: o.id, action: "regenerate" });
    try {
      const r = await api.post(`/offers/${o.id}/generate-pdf`);
      toast.success(`PDF für ${o.offer_number} neu erzeugt`);
      if (r.data?.pdf_storage_path) {
        window.open(freshFileUrl(r.data.pdf_storage_path), "_blank", "noopener");
      }
      onUpdate?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF-Neuerzeugung fehlgeschlagen");
    } finally {
      setBusyOfferId(null);
    }
  };

  const openSendDialog = (offer) => {
    setConfirmRecipient(customer?.email || "");
    // Iter88s: CC vorbelegen mit Sachbearbeiter-E-Mail des Kunden, falls
    // gepflegt. User kann im Feld weitere Adressen mit ";" hinzufügen.
    setConfirmCc((customer?.sachbearbeiter_email || "").trim());
    setConfirmMessage("");
    setConfirmTemplateId("empty");
    setConfirmSendOffer(offer);
  };
  const closeSendDialog = () => {
    setConfirmSendOffer(null);
    setConfirmRecipient("");
    setConfirmCc("");
    setConfirmMessage("");
    setConfirmTemplateId("empty");
    setSending(false);
  };
  const applyTemplate = (tplId) => {
    const tpl = emailTemplates.find((t) => t.id === tplId);
    setConfirmTemplateId(tplId);
    if (!tpl) return;
    const kundeName = [customer?.first_name, customer?.last_name].filter(Boolean).join(" ") || "geehrte Damen und Herren";
    const body = (tpl.body || "")
      .replace(/\{kunde\}/g, kundeName)
      .replace(/\{angebotsdatum\}/g, new Date().toLocaleDateString("de-DE"))
      .replace(/\{angebotsnummer\}/g, confirmSendOffer?.offer_number || "")
      .replace(/\{termin_datum\}/g, "")
      .replace(/\{telefon\}/g, "");
    setConfirmMessage(body);
  };
  const sendOffer = async () => {
    if (!confirmSendOffer) return;
    const recipient = (confirmRecipient || "").trim();
    if (!recipient || !recipient.includes("@")) {
      toast.error("Bitte eine gültige E-Mail-Adresse eingeben");
      return;
    }
    setSending(true);
    try {
      const resp = await api.post(`/offers/${confirmSendOffer.id}/send-email`, {
        to: recipient,
        cc: (confirmCc || "").trim() || undefined,
        custom_message: (confirmMessage || "").trim() || null,
      });
      if (resp.data?.ok === false) {
        toast.error(resp.data?.message || "Versand fehlgeschlagen");
      } else {
        const ccList = resp.data?.cc || [];
        toast.success(
          ccList.length > 0
            ? `Angebot an ${recipient} versendet (CC: ${ccList.join(", ")})`
            : `Angebot an ${recipient} versendet`
        );
        closeSendDialog();
        onUpdate?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  // Iter88zz4 — Kleiner Renderer der Orientierungsangebot-Liste
  // (wird sowohl im Empty-State als auch über der Richtpreis-Liste eingesetzt).
  const renderOrientationBlock = () => {
    if (oaLoading && orientationOffers.length === 0) return null;
    if (!orientationOffers.length) return null;
    return (
      <div data-testid="orientation-offers-block" className="rounded-2xl border border-amber-400/25 bg-amber-400/[0.04] p-4 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-amber-300" />
          <h3 className="font-display text-[15px] font-bold text-zinc-100">
            Orientierungsangebote <span className="text-amber-300">({orientationOffers.length})</span>
          </h3>
          <span className="text-[11px] text-zinc-400 ml-2">unverbindlich · kein LLV · kein Vertrag</span>
        </div>
        <div className="space-y-2">
          {orientationOffers.map((oa) => {
            const pkg = oa.package || {};
            const created = oa.created_at ? new Date(oa.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";
            const sent = oa.status === "sent";
            const price = Number(oa.package_price_gross || 0);
            return (
              <div key={oa.id} data-testid={`orientation-offer-row-${oa.id}`}
                   className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-center rounded-xl bg-[#09090B]/60 border border-zinc-800 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[12.5px] text-amber-300 tracking-widest">{oa.offer_number}</span>
                    {sent
                      ? <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">versendet</span>
                      : <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">generiert</span>}
                    <span className="text-[11px] text-zinc-500">· {created}</span>
                  </div>
                  <div className="text-[13px] text-zinc-200 mt-0.5 truncate">
                    {pkg.brand} {pkg.kw ? `${pkg.kw} kW` : ""} · <b>{fmtEur(price)}</b>
                    {oa.heat_load_kw ? ` · Heizlast ${oa.heat_load_kw} kW` : ""}
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap md:justify-end">
                  <a
                    data-testid={`orientation-offer-pdf-${oa.id}`}
                    href={oa.pdf_storage_path ? `${fileUrl(oa.pdf_storage_path)}&v=${encodeURIComponent(oa.updated_at || oa.created_at || "")}` : "#"}
                    target="_blank" rel="noopener noreferrer"
                    className="h-9 px-3 rounded-lg bg-[#18181B] border border-zinc-700 text-zinc-100 hover:bg-zinc-800 text-[13px] font-semibold inline-flex items-center gap-1.5"
                    title="PDF im neuen Tab öffnen"
                  >
                    <Download size={14} /> PDF
                  </a>
                  <button
                    type="button"
                    data-testid={`orientation-offer-send-${oa.id}`}
                    onClick={() => openOaSendDialog(oa)}
                    disabled={oaSending === oa.id}
                    className="h-9 px-3 rounded-lg bg-amber-400 text-[#09090B] hover:brightness-110 active:scale-95 text-[13px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {oaSending === oa.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    {sent ? "Nochmal senden" : "Per E-Mail senden"}
                  </button>
                  <button
                    type="button"
                    data-testid={`orientation-offer-delete-${oa.id}`}
                    onClick={() => deleteOa(oa)}
                    title="Orientierungsangebot löschen"
                    className="h-9 w-9 rounded-lg bg-[#18181B] border border-zinc-700 text-zinc-400 hover:text-red-300 hover:border-red-500/50 inline-flex items-center justify-center"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderOaSendDialog = () => {
    if (!oaSendDialog) return null;
    return (
      <div className="fixed inset-0 bg-[#09090B]/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
           data-testid="orientation-send-dialog">
        <div className="bg-[#0E0E12] border border-zinc-800 rounded-2xl w-full max-w-lg shadow-2xl">
          <div className="p-5 border-b border-zinc-800 flex items-start justify-between gap-3">
            <div>
              <h3 className="font-display text-lg font-bold text-zinc-100">Orientierungsangebot versenden</h3>
              <p className="text-[12px] text-zinc-400 mt-0.5">{oaSendDialog.oa?.offer_number} · unverbindlich, ohne Vertragsbindung</p>
            </div>
            <button onClick={() => setOaSendDialog(null)} className="text-zinc-400 hover:text-zinc-100" data-testid="orientation-send-close">
              <X size={20} />
            </button>
          </div>
          <div className="p-5 space-y-3">
            <AttachmentPreview url={`/orientation-offers/${oaSendDialog.oa?.id}/attachment-preview`} testid="orientation-send-datasheet" />
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Empfänger *</label>
              <input
                type="email" required
                value={oaSendDialog.recipient}
                onChange={(e) => setOaSendDialog({ ...oaSendDialog, recipient: e.target.value })}
                className="mt-1 w-full h-11 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-amber-400 outline-none"
                data-testid="orientation-send-recipient"
                placeholder="kunde@example.de"
              />
            </div>
            <TeamCopyNote projectId={project.id} />
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">CC (optional · mehrere mit ; oder ,)</label>
              <input
                type="text"
                value={oaSendDialog.cc}
                onChange={(e) => setOaSendDialog({ ...oaSendDialog, cc: e.target.value })}
                className="mt-1 w-full h-11 bg-[#18181B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-amber-400 outline-none"
                data-testid="orientation-send-cc"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-zinc-400 font-semibold">Persönliche Nachricht (optional)</label>
              <textarea
                rows={4}
                value={oaSendDialog.message}
                onChange={(e) => setOaSendDialog({ ...oaSendDialog, message: e.target.value })}
                placeholder="Wenn leer, wird ein neutraler Standard-Text mit KfW-Hinweis versendet."
                className="mt-1 w-full bg-[#18181B] border border-zinc-700 rounded-lg px-3 py-2 text-zinc-50 focus:border-amber-400 outline-none text-[13px]"
                data-testid="orientation-send-message"
              />
            </div>
          </div>
          <div className="p-5 border-t border-zinc-800 flex gap-2 justify-end">
            <button onClick={() => setOaSendDialog(null)} className="h-11 px-4 rounded-lg bg-[#18181B] border border-zinc-700 text-zinc-100 hover:bg-zinc-800 font-semibold">
              Abbrechen
            </button>
            <button
              onClick={sendOrientationOffer}
              disabled={!oaSendDialog.recipient || oaSending === oaSendDialog.oa?.id}
              data-testid="orientation-send-confirm"
              className="h-11 px-4 rounded-lg bg-amber-400 text-[#09090B] hover:brightness-110 active:scale-95 font-bold inline-flex items-center gap-2 disabled:opacity-50"
            >
              {oaSending === oaSendDialog.oa?.id ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              Jetzt versenden
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Empty state
  if (offers.length === 0) {
    return (
      <>
        {renderOrientationBlock()}
        {renderOaSendDialog()}
        <div className="rounded-2xl border border-zinc-800 bg-[#0E0E12] p-6 text-center" data-testid="offers-list-empty">
          <div className="w-14 h-14 rounded-2xl bg-[#00E575]/10 border border-[#00E575]/30 flex items-center justify-center mx-auto mb-4">
            <FileText size={26} className="text-[#00E575]" />
          </div>
          <h2 className="font-display text-xl font-bold text-zinc-100 mb-1.5">
            {orientationOffers.length ? "Noch kein Richtpreisangebot" : "Noch keine Angebote erstellt"}
          </h2>
          <p className="text-[13px] text-zinc-200 font-medium leading-relaxed max-w-md mx-auto mb-4">
            Wechseln Sie zum Tab <b className="text-[#00E575]">„Angebot"</b>, wählen Sie ein WP-Paket
            und tippen Sie auf <b className="text-[#00E575]">„Als Richtpreisangebot erstellen"</b>.
            Sobald ein Richtpreisangebot gespeichert ist, erscheint es hier mit den Buttons
            <b className="text-[#00E575]"> Unterschreiben</b> und <b className="text-[#00E575]">Senden</b>.
          </p>
          <a
            href={`/projects/${project.id}?tab=offer`}
            data-testid="offers-list-empty-cta"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-sm hover:bg-[#00C964] active:scale-95 transition-all"
          >
            <FileText size={16} /> Zum Konfigurator
          </a>
        </div>
      </>
    );
  }

  return (
    <div className="space-y-3" data-testid="offers-list-section">
      {renderOrientationBlock()}
      {renderOaSendDialog()}
      <div className="flex items-end justify-between gap-3 mb-1">
        <div>
          <h2 className="font-display text-xl font-bold text-zinc-100">Richtpreisangebote ({offers.length})</h2>
          <p className="text-[12.5px] text-zinc-200 font-medium mt-0.5">
            Alle für dieses Projekt erstellten verbindlichen Richtpreisangebote – inklusive PDF, Unterschrift und Versand.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-[#00E575]/25 bg-[#00E575]/[0.05] px-3 py-2.5 mb-2 text-[12.5px] text-zinc-100 font-medium flex items-start gap-2">
        <PenTool size={14} className="text-[#00E575] mt-0.5 flex-shrink-0" />
        <span>
          <b className="text-[#00E575]">Unterschreiben</b> für die digitale Kundensignatur am Tablet ·
          <b className="text-[#00E575]"> Senden</b> für PDF-Versand per E-Mail.
        </span>
      </div>

      {/* Iteration 89 — Duplikat-Warnung im Vertriebler-UI. Wenn 2+ aktive
          Versionen existieren, blenden wir einen kompakten Hinweis ein,
          damit der Vertrieb weiß, welche Version gültig ist. Ältere,
          unsignierte Angebote werden weiter unten rot markiert. */}
      {offers.filter((o) => o.status !== "cancelled").length > 1 && (
        <div
          className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-2.5"
          data-testid="offers-duplicate-warning"
        >
          <AlertTriangle size={16} className="text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="text-[12.5px] text-amber-100 font-medium leading-snug">
            Für dieses Projekt existieren <b>{offers.filter((o) => o.status !== "cancelled").length} Angebots-Versionen</b>.
            Nur das mit <b className="text-[#00E575]">„AKTUELL"</b> markierte darf der Kunde unterschreiben —
            ältere QR-Codes sind serverseitig gesperrt.
          </div>
        </div>
      )}

      {offers.map((o, idx) => {
        const editable = o.status !== "signed" && o.status !== "sent";
        const editing = editingOfferId === o.id;
        const canDelete = o.status !== "signed" && (isManagerOrAdmin || o.status !== "sent");
        const isLatest = idx === 0;
        // Veraltet = nicht die aktuelle Version, unsigniert, nicht abgesagt.
        // Signierte alte Versionen bleiben normal dargestellt (waren zum
        // Signieren-Zeitpunkt die aktive Fassung).
        const isOutdated = !isLatest && !o.signature && o.status !== "cancelled" && o.status !== "signed";
        const createdLabel = o.created_at
          ? new Date(o.created_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
          : "";
        // Iter89z-oflabel — Produkt-Linie bestimmen → Linker Farbbalken (B)
        // + Icon-Chip neben Angebotsnummer (A). Fallback via Nummern-Präfix,
        // damit auch Legacy-Angebote ohne offer_type korrekt eingefärbt sind.
        const line = isPvOffer(o) ? PRODUCT_LINE.pv : PRODUCT_LINE.wp;
        const LineIcon = line.icon;
        return (
          <div
            key={o.id}
            className={`bg-[#18181B] border rounded-2xl p-4 space-y-3 ${
              isLatest ? "border-[#00E575]/40"
              : isOutdated ? "border-red-500/40 opacity-70"
              : "border-zinc-800"
            } ${isLatest ? line.cardLatest : line.cardDefault}`}
            data-testid={`offer-${o.id}`}
            data-offer-line={isPvOffer(o) ? "pv" : "wp"}
          >
            {/* Row 1: Title + metadata */}
            <div className="min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-x-2 gap-y-1 min-w-0 flex-wrap">
                  {/* Produkt-Linien-Chip (Variante A). */}
                  <span
                    data-testid={`offer-line-chip-${o.id}`}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10.5px] font-extrabold uppercase tracking-wider ${line.chipClass}`}
                    title={isPvOffer(o) ? "PV-Angebot" : "Wärmepumpen-Angebot"}
                  >
                    <LineIcon size={11} strokeWidth={2.5} />
                    {line.label}
                  </span>
                  <div className="font-mono text-sm text-[#00E575] font-bold whitespace-nowrap" data-testid={`offer-number-${o.id}`}>{o.offer_number}</div>
                  {createdLabel && (
                    <span className="text-[12px] font-mono text-zinc-300 font-semibold whitespace-nowrap">· {createdLabel}</span>
                  )}
                  {isLatest && offers.length > 1 && (
                    <span className="px-1.5 py-0.5 rounded-md text-[9.5px] font-extrabold uppercase tracking-wider bg-[#00E575]/15 text-[#00E575] whitespace-nowrap">Aktuell</span>
                  )}
                  {isOutdated && (
                    <span
                      className="px-1.5 py-0.5 rounded-md text-[9.5px] font-extrabold uppercase tracking-wider bg-red-500/20 text-red-300 whitespace-nowrap"
                      data-testid={`offer-outdated-${o.id}`}
                    >
                      Veraltet · gesperrt
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {o.signature && (
                    <span className="px-2 py-0.5 rounded-md text-[10.5px] font-extrabold uppercase tracking-wider bg-[#00E575]/15 text-[#00E575]">✓ Unterschrieben</span>
                  )}
                  {o.discount_eur_applied > 0 && (
                    <span className="px-2 py-0.5 rounded-md text-[10.5px] font-extrabold uppercase tracking-wider bg-amber-500/15 text-amber-300">Rabatt: -{fmtEur(o.discount_eur_applied)}</span>
                  )}
                </div>
              </div>
              <div className="font-display text-[16px] font-bold mt-1 break-words sm:truncate text-zinc-100" title={o.package_name}>{o.package_name}</div>
              {o.signature?.signed_at && (
                <div className="mt-1 flex items-center gap-1.5 text-[12px] text-[#00E575] font-medium" data-testid={`offer-${o.id}-signed-meta`}>
                  <Check size={12} />
                  <span>
                    Digital signiert
                    {o.signature.signer_name ? <> von <b className="text-zinc-100">{o.signature.signer_name}</b></> : null}
                    {o.signature.signed_at_display
                      ? <> · {o.signature.signed_at_display}</>
                      : <> · {new Date(o.signature.signed_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}</>}
                    {o.signature.channel === "public-qr" ? <span className="ml-1 px-1.5 py-0.5 rounded bg-[#00E575]/15 text-[10px] uppercase tracking-wider font-bold">QR</span> : null}
                  </span>
                </div>
              )}
              {o.signature?.signed_at && isAdmin(user) && (
                <div className="mt-1.5">
                  <RevokeSignatureButton offer={o} onDone={onUpdate} />
                </div>
              )}
              <RevokedSignatureNote offer={o} />
              {/* Iter88zao — vom Kunden gewählte Zusatz-Optionen anzeigen */}
              {Array.isArray(o.signing_options_selected) && o.signing_options_selected.filter((so) => so.checked).length > 0 && (
                <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`offer-${o.id}-selected-options`}>
                  {o.signing_options_selected.filter((so) => so.checked).map((so) => (
                    <span key={so.option_id} className="text-[10.5px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" title={so.description || ""}>
                      ✓ {so.title}
                    </span>
                  ))}
                </div>
              )}
              {o.telesales_audit && (
                <div className="mt-1 flex items-center gap-1.5 text-[12px] text-zinc-300 font-medium" data-testid={`offer-${o.id}-audit`}>
                  <Phone size={11} className="text-[#00E575]" />
                  <span>Aus Telesales-Angebot von <b className="text-zinc-100">{o.telesales_audit.agent_name}</b>{o.telesales_audit.captured_at ? ` · ${new Date(o.telesales_audit.captured_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}` : ""}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 text-[12.5px] text-zinc-200 mt-0.5 font-medium">
                <span>Status: <span className="text-zinc-100 font-bold">{o.status}</span></span>
                <span className="font-mono text-zinc-100 font-bold text-[16px] sm:text-[12.5px]" data-testid={`offer-${o.id}-total-gross`}>
                  {o.selling_price_gross ? fmtEur(o.selling_price_gross) : fmtEur(o.total_gross)}
                </span>
              </div>
              {/* Iter89q (2026-02-24) — Mail-Versand-Historie pro Angebot */}
              <OfferSendHistory offerId={o.id} />
              {/* Iter88zbw Phase B — Mehrverkauf-Anzeige (nur wenn gesetzt) */}
              {o.selling_price_gross && o.upsell_delta_gross > 0 && (
                <div className="mt-1.5 rounded-lg bg-[#00E575]/10 border border-[#00E575]/30 px-2.5 py-1.5 text-[11.5px] flex items-center gap-2 flex-wrap" data-testid={`offer-${o.id}-upsell-info`}>
                  <span className="text-[#00E575] font-mono uppercase tracking-wider font-bold">Mehrverkauf</span>
                  <span className="text-zinc-100 font-mono">+{fmtEur(o.upsell_delta_gross)}</span>
                  <span className="text-zinc-400">
                    Split {o.upsell_split_rep_pct}/{100 - o.upsell_split_rep_pct} · Vertrieb {fmtEur(o.upsell_rep_share)} · Novatus {fmtEur(o.upsell_novatus_share)}
                  </span>
                </div>
              )}
              <UpsellEditor offer={o} onChanged={onUpdate} />
              {/* Iteration 91 — Diff zur Vorversion. Nur beim aktuellsten
                  Angebot einblenden (wenn changes_vs_previous existiert),
                  damit der Vertrieb auf einen Blick sieht, was er beim
                  Neu-Rechnen geändert hat. */}
              {isLatest && o.changes_vs_previous && (
                <OfferDiffBlock diff={o.changes_vs_previous} />
              )}
            </div>

            {/* Row 2: Action buttons */}
            <div className="grid grid-cols-2 gap-2">
              {o.pdf_storage_path ? (
                <a
                  href={fileUrl(o.pdf_storage_path)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`offer-pdf-${o.id}`}
                  className={`h-11 rounded-xl text-[13.5px] font-bold inline-flex items-center justify-center gap-1.5 border ${
                    o.signature
                      ? "bg-[#00E575]/15 hover:bg-[#00E575]/25 text-[#00E575] border-[#00E575]/50"
                      : "bg-zinc-800 hover:bg-zinc-700 text-zinc-100 border-transparent font-semibold"
                  }`}
                  title={o.signature ? "Angebots-PDF inkl. Kunden-Unterschrift herunterladen" : "Angebots-PDF herunterladen"}
                >
                  <Download size={16} /> {o.signature ? "Signiertes Angebot" : "PDF"}
                </a>
              ) : o.signature ? (
                <button
                  onClick={() => rebuildSignedPdf(o)}
                  disabled={busyOfferId?.id === o.id && busyOfferId?.action === "rebuild"}
                  data-testid={`offer-rebuild-signed-pdf-${o.id}`}
                  className="h-11 rounded-xl bg-amber-500/15 text-amber-300 border border-amber-500/40 font-bold text-[13.5px] inline-flex items-center justify-center gap-1.5 hover:bg-amber-500/25 disabled:opacity-60"
                  title="Signiertes PDF wurde nicht gespeichert (Hintergrund-Job) — jetzt aus der gespeicherten Unterschrift neu erzeugen."
                >
                  {busyOfferId?.id === o.id && busyOfferId?.action === "rebuild"
                    ? <><Loader2 className="animate-spin" size={16} /> Erzeuge…</>
                    : <><FileText size={16} /> Signiertes PDF erzeugen</>}
                </button>
              ) : (
                <span className="h-11 rounded-xl bg-zinc-900 text-zinc-300 text-[12px] font-semibold inline-flex items-center justify-center">Kein PDF</span>
              )}
              {/* Iteration 84: Liefer- und Leistungsvertrag für signierte
                  Angebote — sofort abrufbar in der Projektakte, unabhängig
                  vom BzA-Datenpaket-Versand. */}
              {(o.signature || o.status === "signed") ? (
                o.liefer_leistung_pdf_path ? (
                  <a
                    href={fileUrl(o.liefer_leistung_pdf_path)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`offer-liefer-leistung-${o.id}`}
                    className="h-11 rounded-xl bg-[#00E575]/15 hover:bg-[#00E575]/25 text-[#00E575] border border-[#00E575]/50 text-[13.5px] font-bold inline-flex items-center justify-center gap-1.5"
                    title="Liefer- und Leistungsvertrag (PDF, unterschrieben)"
                  >
                    <Download size={16} /> Liefer-/Leistungsvertrag
                  </a>
                ) : (
                  <button
                    onClick={() => buildLieferLeistung(o)}
                    disabled={busyOfferId?.id === o.id && busyOfferId?.action === "liefer"}
                    data-testid={`offer-liefer-leistung-build-${o.id}`}
                    className="h-11 rounded-xl bg-[#00E575]/10 text-[#00E575] border border-[#00E575]/40 font-bold text-[13.5px] inline-flex items-center justify-center gap-1.5 hover:bg-[#00E575]/20 disabled:opacity-60"
                    title="Liefer- und Leistungsvertrag erzeugen (unabhängig vom BzA-Paket-Versand)"
                  >
                    {busyOfferId?.id === o.id && busyOfferId?.action === "liefer"
                      ? <><Loader2 className="animate-spin" size={16} /> Erzeuge…</>
                      : <><FileText size={16} /> Liefer-/Leistungsvertrag</>}
                  </button>
                )
              ) : (
                // Iter88zz9 — UN-signiert: „PDF neu erzeugen"-Button, damit
                // Katalog-/Beschreibungs-Updates ohne Neuanlage sichtbar werden.
                <button
                  onClick={() => regenerateOfferPdf(o)}
                  disabled={busyOfferId?.id === o.id && busyOfferId?.action === "regenerate"}
                  data-testid={`offer-regenerate-pdf-${o.id}`}
                  className="h-11 rounded-xl bg-sky-500/10 text-sky-300 border border-sky-500/40 font-bold text-[13.5px] inline-flex items-center justify-center gap-1.5 hover:bg-sky-500/20 disabled:opacity-60"
                  title="PDF neu erzeugen (übernimmt aktuelle Katalog-Beschreibungen, Preise, Marge)"
                >
                  {busyOfferId?.id === o.id && busyOfferId?.action === "regenerate"
                    ? <><Loader2 className="animate-spin" size={16} /> Erzeuge…</>
                    : <><RefreshCw size={16} /> PDF neu erzeugen</>}
                </button>
              )}
              {/* Iteration 82: Easybill-Push direkt aus der Projektakte. Zeigt Badge
                  (grün) wenn schon gepusht, sonst Push-Button. Für sales_rep versteckt. */}
              <div className="col-span-2 flex justify-end -mt-1">
                <EasybillPushButton offerId={o.id} />
              </div>
              {editable && (
                <button
                  onClick={() => (editing ? setEditingOfferId(null) : openEditOffer(o))}
                  data-testid={`offer-edit-${o.id}`}
                  className={`h-11 rounded-xl text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 transition border ${editing ? "bg-zinc-700 border-zinc-600 text-zinc-100" : "bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-zinc-100"}`}
                >
                  {editing ? <><X size={16} /> Abbrechen</> : <><Pencil size={16} /> Bearbeiten</>}
                </button>
              )}
              {/* Iter89k (2026-02-20) — Konfigurator-Button war früher auf
                  `status==="draft"` beschränkt; User (Christof) meldete, dass
                  bei realen Angeboten fast immer `generated`/`sent` steht und
                  der Button dann unsichtbar war. Neue Regel: Sichtbar für
                  ALLE nicht-signierten, nicht-abgelösten, nicht-abgebrochenen
                  Angebote. Wenn das Angebot schon `sent_at` hat, wird beim
                  Speichern eine neue Angebotsnummer erzeugt (der bestehende
                  „Als neues Angebot"-Button im Konfigurator greift dann); die
                  Rechts-Logik regelt der Backend-Save-Flow selbst. */}
              {!o.signature && !["signed", "superseded", "cancelled"].includes(o.status) && (
                <button
                  onClick={() => navigate(`/projects/${o.project_id}?tab=offer&edit=${o.id}`)}
                  data-testid={`offer-edit-configurator-${o.id}`}
                  className="h-11 rounded-xl bg-amber-500/15 text-amber-300 border border-amber-500/40 font-bold text-[13.5px] inline-flex items-center justify-center gap-1.5 hover:bg-amber-500/25"
                  title={o.sent_at
                    ? "Angebot im Konfigurator öffnen. Änderungen werden als NEUES Angebot mit neuer Nummer gespeichert; das aktuelle Angebot wird abgelöst."
                    : "Paket / Komponenten umkonfigurieren — Angebotsnummer bleibt (wichtig für KfW-458)"}
                >
                  <Pencil size={16} /> Konfigurator
                </button>
              )}
              {!o.signature && (
                <button
                  onClick={() => setSigningOfferId(o.id)}
                  data-testid={`offer-sign-${o.id}`}
                  className="h-11 rounded-xl bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 font-bold text-[13.5px] inline-flex items-center justify-center gap-1.5 hover:bg-[#00E575]/25"
                >
                  <PenTool size={16} /> Unterschreiben
                </button>
              )}
              {/* Iter88zm (2026-08-15): Manuelle Signatur-Erfassung für per
                  E-Mail/Post/Fax zurückgesendete Zustimmungen. */}
              {!o.signature && (
                <button
                  onClick={() => setManualSignOfferId(o.id)}
                  data-testid={`offer-manual-sign-${o.id}`}
                  title="Signatur nachträglich erfassen (E-Mail/Post/Fax)"
                  className="h-11 rounded-xl bg-blue-500/15 text-blue-300 border border-blue-500/40 font-bold text-[13.5px] inline-flex items-center justify-center gap-1.5 hover:bg-blue-500/25"
                >
                  <Mail size={16} /> Manuell erfassen
                </button>
              )}
              <button
                onClick={() => openSendDialog(o)}
                data-testid={`offer-send-${o.id}`}
                className="col-span-2 sm:col-span-1 order-first sm:order-none h-12 sm:h-11 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[14.5px] sm:text-[13.5px] inline-flex items-center justify-center gap-1.5 hover:bg-[#00C964] active:scale-[0.98] transition-transform"
              >
                <Send size={16} /> Senden
              </button>
              {canDelete && (
                <button
                  onClick={() => deleteOffer(o)}
                  disabled={deletingOfferId === o.id}
                  data-testid={`offer-delete-${o.id}`}
                  className="h-11 rounded-xl bg-red-500/10 text-red-400 border border-red-500/30 font-semibold text-[13.5px] inline-flex items-center justify-center gap-1.5 hover:bg-red-500/20 hover:border-red-500/60 disabled:opacity-50"
                  title="Angebot löschen"
                >
                  {deletingOfferId === o.id ? <Loader2 className="animate-spin" size={16} /> : <><Trash2 size={16} /> Löschen</>}
                </button>
              )}
            </div>

            {/* Edit panel */}
            {editing && (
              <div
                ref={(el) => { editPanelRefs.current[o.id] = el; }}
                className="mt-1 pt-3 border-t border-zinc-800 space-y-3"
                data-testid={`offer-edit-panel-${o.id}`}
              >
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Tag size={14} className="text-[#00E575]" />
                    <span className="text-[12px] font-extrabold uppercase tracking-wider text-zinc-100">Rabatt anpassen</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10.5px] uppercase tracking-wider text-zinc-300 font-bold mb-1">in %</label>
                      <input
                        data-testid={`offer-edit-pct-${o.id}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={editPct}
                        onChange={(e) => setEditPct(e.target.value)}
                        placeholder="0,00"
                        className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-sm text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-[10.5px] uppercase tracking-wider text-zinc-300 font-bold mb-1">in €</label>
                      <input
                        data-testid={`offer-edit-eur-${o.id}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={editEur}
                        onChange={(e) => setEditEur(e.target.value)}
                        placeholder="0,00"
                        className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-sm text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
                      />
                    </div>
                  </div>
                  {(caps.max_discount_percent != null || caps.max_discount_eur != null) && !isManagerOrAdmin && (
                    <p className="text-[12px] text-zinc-300 font-medium mt-1.5">
                      Max. erlaubter Rabatt: {caps.max_discount_percent != null ? `${caps.max_discount_percent}%` : "—"}
                      {caps.max_discount_percent != null && caps.max_discount_eur != null ? " oder " : ""}
                      {caps.max_discount_eur != null ? fmtEur(caps.max_discount_eur) : ""}
                      {" (kleineres greift)"}
                    </p>
                  )}
                </div>

                {/* Manager-only margin override — Iter88e (2026-02-19): pro Angebot
                    deaktiviert. Die Provision wird zentral pro Mandant/User in der
                    Membership gepflegt; ein per-Angebot-Override hat mehr Verwirrung
                    als Nutzen gestiftet ("bei DSS soll die Marge einmal zentral gesetzt
                    werden"). Bei Bedarf via Feature-Flag reaktivierbar.
                {isManagerOrAdmin && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={14} className="text-amber-400" />
                      <span className="text-[12px] font-extrabold uppercase tracking-wider text-zinc-100">Provision für dieses Angebot</span>
                    </div>
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={() => setEditMarginType("percent")}
                        className={`flex-1 h-9 rounded-lg text-[12.5px] font-bold border transition ${editMarginType === "percent" ? "border-[#00E575] bg-[#00E575]/15 text-[#00E575]" : "border-zinc-700 bg-[#09090B] text-zinc-300"}`}
                      >Prozent</button>
                      <button
                        onClick={() => setEditMarginType("eur")}
                        className={`flex-1 h-9 rounded-lg text-[12.5px] font-bold border transition ${editMarginType === "eur" ? "border-[#00E575] bg-[#00E575]/15 text-[#00E575]" : "border-zinc-700 bg-[#09090B] text-zinc-300"}`}
                      >Euro</button>
                    </div>
                    <div className="relative">
                      <input
                        data-testid={`offer-edit-margin-${o.id}`}
                        type="number"
                        min="0"
                        step={editMarginType === "percent" ? "0.5" : "10"}
                        value={editMarginValue}
                        onChange={(e) => setEditMarginValue(e.target.value)}
                        placeholder={editMarginType === "percent" ? "z. B. 15" : "z. B. 1500"}
                        className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 pr-9 text-sm text-zinc-100 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-300 font-semibold text-xs">{editMarginType === "percent" ? "%" : "€"}</span>
                    </div>
                    <p className="text-[12px] text-zinc-300 font-medium mt-1">Leer lassen → ursprüngliche Provision beibehalten.</p>
                  </div>
                )}
                */}

                <button
                  data-testid={`offer-edit-save-${o.id}`}
                  onClick={saveEditedOffer}
                  disabled={editSaving}
                  className="h-11 w-full bg-[#00E575] text-[#09090B] font-bold rounded-xl hover:bg-[#00C964] active:scale-[0.97] transition flex items-center justify-center gap-2"
                >
                  {editSaving ? <Loader2 className="animate-spin" /> : <><Check size={16} /> Neu berechnen & PDF aktualisieren</>}
                </button>
              </div>
            )}

            {signingOfferId === o.id && (
              <div className="pt-3 border-t border-zinc-800">
                <SignaturePad
                  signerNameDefault={`${customer?.first_name || ""} ${customer?.last_name || ""}`.trim()}
                  saving={signSaving}
                  onCancel={() => setSigningOfferId(null)}
                  onSave={(dataUrl, signerName) => signOffer(o.id, dataUrl, signerName)}
                />
              </div>
            )}
          </div>
        );
      })}

      {/* Send-confirmation modal */}
      {confirmSendOffer && (
        <div
          data-testid="offer-send-modal"
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 py-4 sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) closeSendDialog(); }}
        >
          <div className="w-full max-w-md max-h-[calc(100dvh-2rem)] flex flex-col bg-[#18181B] border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden" data-testid="offer-send-modal-panel">
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-800 flex items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-full bg-[#00E575]/15 border border-[#00E575]/30 flex items-center justify-center shrink-0">
                  <Send size={16} className="text-[#00E575]" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-[16px] font-bold leading-tight truncate">Angebot versenden</h3>
                  <p className="text-[11.5px] text-zinc-300 font-semibold truncate">{confirmSendOffer.offer_number}</p>
                </div>
              </div>
              <button
                data-testid="offer-send-modal-close"
                onClick={closeSendDialog}
                className="text-zinc-300 hover:text-zinc-100 w-10 h-10 -mr-2 inline-flex items-center justify-center rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-4 sm:px-5 py-4 sm:py-5 space-y-4 overflow-y-auto flex-1 min-h-0" data-testid="offer-send-modal-body">
              <AttachmentPreview url={`/offers/${confirmSendOffer.id}/attachment-preview`} testid="offer-send-datasheet" />
              <div>
                <label className="text-[11.5px] uppercase font-extrabold tracking-wider text-zinc-100">
                  Empfänger-E-Mail
                </label>
                <input
                  data-testid="offer-send-recipient-input"
                  type="email"
                  value={confirmRecipient}
                  onChange={(e) => setConfirmRecipient(e.target.value)}
                  autoComplete="off"
                  className="mt-2 h-12 w-full rounded-xl bg-[#09090B] border border-zinc-700 px-3 text-zinc-100 text-[15px] focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
                  placeholder="kunde@example.de"
                />
                <p className="text-[11.5px] text-zinc-200 font-medium mt-1.5">
                  {customer?.email
                    ? "Vorbelegt aus dem Kundendatensatz. Du kannst hier einmalig eine andere Adresse eintragen."
                    : "Keine E-Mail im Kundendatensatz hinterlegt — bitte hier eintragen. Eine Unterschrift ist NICHT erforderlich für den Versand."}
                </p>
              </div>

              <TeamCopyNote projectId={project.id} />
              {/* Iter88s: CC-Empfänger. Automatisch vorbelegt mit dem
                  Sachbearbeiter-E-Mail des Kunden. Mehrere Adressen mit ";"
                  oder "," trennen.
                  Iter88zbs: Autofill-Historie (LocalStorage + Backend). */}
              <div>
                <label className="text-[11.5px] uppercase font-extrabold tracking-wider text-zinc-100">
                  CC (optional)
                </label>
                <AutofillInput
                  dataTestid="offer-send-cc-input"
                  storageKey="cc_email"
                  type="text"
                  value={confirmCc}
                  onChange={setConfirmCc}
                  autoComplete="email"
                  splitOn={[";", ","]}
                  className="mt-2 h-12 w-full rounded-xl bg-[#09090B] border border-zinc-700 px-3 text-zinc-100 text-[15px] focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
                  placeholder="sachbearbeiter@..; buchhaltung@.."
                />
                <p className="text-[11.5px] text-zinc-200 font-medium mt-1.5">
                  {customer?.sachbearbeiter_email
                    ? `Vorbelegt mit dem Sachbearbeiter „${customer?.sachbearbeiter_name || customer?.sachbearbeiter_email}". Mehrere Adressen mit ";" trennen.`
                    : `Optional. Mehrere Adressen mit „;" oder „," trennen — z.B. Innendienst + Buchhaltung.`}
                </p>
              </div>

              {/* Iter88t: Textbaustein + persönliche Nachricht */}
              <div>
                <label className="text-[11.5px] uppercase font-extrabold tracking-wider text-zinc-100">
                  Textbaustein
                </label>
                <select
                  data-testid="offer-send-template-select"
                  value={confirmTemplateId}
                  onChange={(e) => applyTemplate(e.target.value)}
                  className="mt-2 h-12 w-full rounded-xl bg-[#09090B] border border-zinc-700 px-3 text-zinc-100 text-[15px] focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
                >
                  {emailTemplates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                <p className="text-[11.5px] text-zinc-200 font-medium mt-1.5">
                  {(emailTemplates.find((t) => t.id === confirmTemplateId)?.description) || "Wähle einen Standardtext."}
                </p>
              </div>

              <div>
                <label className="text-[11.5px] uppercase font-extrabold tracking-wider text-zinc-100">
                  Persönliche Nachricht (optional)
                </label>
                <textarea
                  data-testid="offer-send-message-input"
                  value={confirmMessage}
                  onChange={(e) => setConfirmMessage(e.target.value)}
                  rows={3}
                  placeholder="Sehr geehrter Herr Müller, im Anschluss an unser Gespräch …"
                  className="mt-2 w-full rounded-xl bg-[#09090B] border border-zinc-700 px-3 py-2 text-zinc-100 text-[14px] focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none resize-none"
                />
              </div>

              <div className="rounded-xl bg-[#09090B] border border-zinc-800 p-3 text-[12.5px] text-zinc-200 font-medium leading-relaxed">
                <div className="font-extrabold text-zinc-100 mb-1">Was bekommt der Kunde?</div>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Personalisierte Anrede („Guten Tag, Herr/Frau {customer?.last_name || "—"}")</li>
                  <li>Begleittext im NOVATUS-Wording</li>
                  <li>Vollständiges Angebot als PDF im Anhang</li>
                  <li>Link zum Projekt-Status-Tracker</li>
                </ul>
              </div>
            </div>

            <div className="px-4 sm:px-5 py-3 sm:py-4 bg-[#09090B] border-t border-zinc-800 flex items-center gap-2 shrink-0">
              <button
                data-testid="offer-send-modal-cancel"
                onClick={closeSendDialog}
                disabled={sending}
                className="flex-1 h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-bold text-[14px] disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                data-testid="offer-send-modal-confirm"
                onClick={sendOffer}
                disabled={sending}
                className="flex-1 h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[14px] hover:bg-[#00C964] flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.97] transition-all"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? "Sende…" : "Senden"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Iter88zm — Manuelle Signatur-Erfassung */}
      {manualSignOfferId && (
        <ManualSignModal
          offerId={manualSignOfferId}
          offerNumber={(offers.find(o => o.id === manualSignOfferId) || {}).offer_number}
          open={!!manualSignOfferId}
          onClose={() => setManualSignOfferId(null)}
          onSaved={() => { setManualSignOfferId(null); onUpdate?.(); }}
        />
      )}
    </div>
  );
}
/**
 * OfferDiffBlock — Iteration 91.
 *
 * Zeigt kompakt, was sich zum letzten Angebot des Projekts geändert hat:
 * Δ Gesamtpreis, Δ Rabatt, neue/entfernte Komponenten, Mengen-/Preis-
 * Änderungen an bestehenden Positionen. Standardmäßig eingeklappt, damit
 * die Angebotsliste kompakt bleibt.
 */
function OfferDiffBlock({ diff }) {
  const [open, setOpen] = useState(false);
  if (!diff) return null;
  const {
    vs_offer_number, total_gross_delta, discount_delta,
    items_added = [], items_removed = [], items_changed = [], item_change_count = 0,
  } = diff;
  const priceUp = total_gross_delta > 0.005;
  const priceDown = total_gross_delta < -0.005;
  const priceLabel = fmtEur(Math.abs(total_gross_delta));
  return (
    <div
      className="mt-2 rounded-xl border border-zinc-700 bg-zinc-900/60 overflow-hidden"
      data-testid={`offer-diff-block`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-zinc-800/60"
        data-testid="offer-diff-toggle"
      >
        <div className="flex items-center gap-1.5 text-[12px] text-zinc-200 font-medium min-w-0">
          <History size={13} className="text-zinc-400 flex-shrink-0" />
          <span className="truncate">
            Änderungen ggü. <b className="text-zinc-100 font-mono">{vs_offer_number}</b>
          </span>
          {item_change_count > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded bg-zinc-800 text-[10px] font-bold text-zinc-200">
              {item_change_count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {(priceUp || priceDown) && (
            <span className={`text-[12px] font-bold ${priceUp ? "text-red-300" : "text-[#00E575]"}`}>
              {priceUp ? <ArrowUp size={12} className="inline -mt-0.5" /> : <ArrowDown size={12} className="inline -mt-0.5" />}
              {" "}{priceLabel}
            </span>
          )}
          <ChevronDown size={14} className={`text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="px-3 py-2.5 border-t border-zinc-800 space-y-2" data-testid="offer-diff-body">
          <div className="grid grid-cols-2 gap-2 text-[11.5px]">
            <div className="text-zinc-400 font-medium">Δ Gesamt</div>
            <div className={`text-right font-bold ${priceUp ? "text-red-300" : priceDown ? "text-[#00E575]" : "text-zinc-300"}`}>
              {total_gross_delta >= 0 ? "+" : "−"}{fmtEur(Math.abs(total_gross_delta))}
            </div>
            {Math.abs(discount_delta) > 0.005 && (
              <>
                <div className="text-zinc-400 font-medium">Δ Rabatt</div>
                <div className={`text-right font-bold ${discount_delta > 0 ? "text-amber-300" : "text-zinc-300"}`}>
                  {discount_delta >= 0 ? "+" : "−"}{fmtEur(Math.abs(discount_delta))}
                </div>
              </>
            )}
          </div>
          {items_added.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-[#00E575]/80 mb-1">Neu hinzugefügt</div>
              <ul className="space-y-1">
                {items_added.map((it, i) => (
                  <li key={`add-${i}`} className="text-[11.5px] text-zinc-100 flex items-center justify-between gap-2">
                    <span className="truncate flex-1"><span className="text-[#00E575] mr-1">+</span>{it.name || "—"}</span>
                    <span className="text-zinc-300 font-mono flex-shrink-0">{it.quantity}× {fmtEur(it.line_net)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {items_removed.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-red-300/80 mb-1">Entfernt</div>
              <ul className="space-y-1">
                {items_removed.map((it, i) => (
                  <li key={`rem-${i}`} className="text-[11.5px] text-zinc-300 flex items-center justify-between gap-2 line-through">
                    <span className="truncate flex-1"><span className="text-red-300 mr-1 no-underline">−</span>{it.name || "—"}</span>
                    <span className="text-zinc-400 font-mono flex-shrink-0">{it.quantity}× {fmtEur(it.line_net)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {items_changed.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-amber-300/80 mb-1">Geändert</div>
              <ul className="space-y-1">
                {items_changed.map((it, i) => (
                  <li key={`chg-${i}`} className="text-[11.5px] text-zinc-100">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate flex-1 font-medium">{it.name || "—"}</span>
                      <span className={`font-mono font-bold flex-shrink-0 ${it.line_delta > 0 ? "text-red-300" : "text-[#00E575]"}`}>
                        {it.line_delta >= 0 ? "+" : "−"}{fmtEur(Math.abs(it.line_delta))}
                      </span>
                    </div>
                    <div className="text-zinc-400 text-[10.5px] font-mono">
                      {it.quantity_from !== it.quantity_to && <>Menge {it.quantity_from} → {it.quantity_to} · </>}
                      {it.unit_from !== it.unit_to && <>Preis {fmtEur(it.unit_from)} → {fmtEur(it.unit_to)}</>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {item_change_count === 0 && Math.abs(total_gross_delta) < 0.005 && (
            <div className="text-[11.5px] text-zinc-400 italic">Keine Positions-Änderungen — nur Metadaten aktualisiert.</div>
          )}
        </div>
      )}
    </div>
  );
}
